import { ApiError } from '@/api/client';

/**
 * Traduce un fallo de las acciones de «Gestión de usuarios» a la clave i18n (`frontlineUsers.err.*`).
 *
 * El backend responde con un `reason` estable: en el cuerpo directo (`{ reason }`, los 404/409/429 de los
 * comandos) o dentro de `errors.reason` (las `UserAdminDomainException` que el middleware vuelve 422). Se
 * decide por ese código y no por el texto, que sale solo en español y la pantalla habla 7 idiomas.
 */
export function frontlineErrorKey(err: unknown): string {
  const base = 'frontlineUsers.err.';
  if (!(err instanceof ApiError)) return `${base}generic`;

  const reason = extractReason(err.body);
  switch (reason) {
    case 'ficha-no-encontrada': return `${base}notFound`;
    case 'ya-verificado': return `${base}alreadyVerified`;
    case 'sin-correo': return `${base}noEmail`;
    case 'ficha-inactiva': return `${base}inactive`;
    case 'ficha-sin-reclamar': return `${base}notClaimed`;
    case 'correo-no-verificado': return `${base}emailNotVerified`;
    case 'demasiados-envios': return `${base}quota`;
    case 'envio-fallido': return `${base}sendFailed`;
    case 'correo-invalido': return `${base}invalid`;
    case 'correo-en-uso': return `${base}taken`;
    case 'registro-pendiente': return `${base}pending`;
    case 'correo-obligatorio': return `${base}required`;
    // Elegibilidad para activar una ficha del padrón (mismas guardas que el registro y la activación).
    case 'cuenta-del-ad': return `${base}directory`;
    case 'ficha-de-directorio':
    case 'acceso-por-sso':
    case 'no-le-toca-registro-sino-sso':
    case 'ficha-con-dominio-corporativo': return `${base}sso`;
    case 'funcion-no-registrable': return `${base}jobFunction`;
    case 'ou-bloqueada-para-registro': return `${base}ouBlocked`;
    case 'ficha-de-admin': return `${base}adminRecord`;
    default:
      // Un 409 sin motivo conocido es una guarda de elegibilidad del backend (OU bloqueada, ficha con contraseña…).
      if (err.status === 409) return `${base}notEligible`;
      if (err.status === 404) return `${base}notFound`;
      if (err.status === 429) return `${base}quota`;
      return `${base}generic`;
  }
}

function extractReason(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const direct = (body as { reason?: unknown }).reason;
  if (typeof direct === 'string') return direct;
  const nested = (body as { errors?: { reason?: unknown } }).errors?.reason;
  return typeof nested === 'string' ? nested : null;
}
